<script lang="ts">
  import './game-board.less';
  import {
    playerInstance,
    enemyInstance,
    dieHistory,
    actionHistory,
    techniqueHistory,
    lockedDie,
    activePlayer,
    inactivePlayer,
    options,
    screens,
    peekedDie,
  } from '../store';
  import type { DieCreation, Die as DieType } from './models/die.type';
  import HealthBar from './components/health-bar.svelte';
  import HistoryBar from './components/history-bar.svelte';
  import Particles from './components/particle.svelte';
  import Portrait from './components/portrait.svelte';
  import DiceTray from './dice-tray/dice-tray.svelte';
  import DieElement from './dice-tray/die.svelte';
  import Icon from './icon/icon.svelte';
  import { createDie, DieMaterial } from './models/die.type';
  import {
    filterTechniques,
    validateTechnique,
    isTechniquePossible,
    type Technique,
    fitDieInTechnique,
    getAvailableTechniques,
    sortTechniques,
  } from './models/techniques.type';
  import type { Modifier } from './models/modifier';
  import Hover from './components/hover.svelte';
  import Text from './components/text.svelte';
  import Draggable from './components/draggable.svelte';
  import Droppable from './components/droppable.svelte';
  import { clonePlayer, getModifiers, type Player } from './models/player.type';
  import type { Battle } from './models/battle.type';
  import { openOptions } from './modals/modals';
  import { onMount } from 'svelte';
  import { get, writable } from 'svelte/store';
  import { continueTutorial } from './utils/tutorial';
  import type { HistoryItem } from './models/history.type';

  import { flip } from 'svelte/animate';
  import { send, receive } from '../animation';

  import { randomFloat, clamp, distance, randomInt } from './utils/math';

  import { calculateMazlovs, chooseTechnique } from './utils/ai';
  import { DieFace } from './models/face.type';
  import Chrome from './components/chrome.svelte';
  import { alert } from './modals/modals';
  import { clone, type DropEvent } from './utils/utils';
  import { playAudio, SoundType } from './components/audio.svelte';
  import { openGlossary } from './glossary/glossary';

  import { addModifier } from './models/player.type';
  import { StatusEffect } from './models/status-effects.type';
  import { AmmoType } from './models/ammo.type';
  import { Songs } from './models/status-effects/song';
  import { Stances } from './models/status-effects/stance';
  import { Screen } from './models/screen.type';
  import { BattleState } from './models/battle.type';
  import { TutorialEvent } from './utils/tutorial';

  interface Props {
    data: {
      player: Player;
      enemy: Player;
      battle: Battle;
    };
  }

  let { data }: Props = $props();

  /*
    The board changes players, dice and techniques in place and then says so:
    updatePlayers() re-sets the player stores, updateDice() rebuilds the dice
    lists. In runes mode a value only redraws when it is a new one, so what the
    markup reads is either rebuilt (the $state.raw lists), or read through a
    store, where setting the same object again still counts as a change.
  */
  let isPlayerActive = $state(true);
  let gameState: BattleState = $state(BattleState.PreBattle);
  let positions: Array<Array<number>> = $state([]);

  let lockedDieUI: DieType[] = $state.raw([]);
  let techniqueHistoryUI: HistoryItem[] = $state.raw([]);

  /*
    Points activePlayer and inactivePlayer at whoever's turn it is.

    get() rather than $playerInstance, because this runs inside the history
    stores' own subscriptions. In runes mode `$store` subscribes lazily, on
    first read, so the board's own subscription is queued behind these ones and
    still holds the old value while they run. After execute() put() a clone, the
    technique then acted on the snapshot just saved for undo -- and undo brought
    its effects back.
  */
  function syncActivePlayers() {
    const player = get(playerInstance);
    const enemy = get(enemyInstance);
    activePlayer.set(isPlayerActive ? player : enemy);
    inactivePlayer.set(isPlayerActive ? enemy : player);
  }

  const unsubscribePlayer = playerInstance.subscribe(syncActivePlayers);
  const unsubscribeEnemy = enemyInstance.subscribe(syncActivePlayers);
  playerInstance.set(data.player);
  enemyInstance.set(data.enemy);

  /** A player's dice, and any their traits and items add, as shown while waiting. */
  function buildDice(player: Player, prefix: string): Array<DieType> {
    let creations: Array<DieCreation> = clone(player.dice);
    getModifiers(player, 'getDice').forEach((mod) => {
      if (mod.getDice) {
        mod.getDice(creations).forEach((die) => {
          creations = creations.concat(die);
        });
      }
    });
    return creations.map((die: DieCreation, index: number) => {
      return createDie(die, index, prefix + index);
    });
  }

  const playerDie = buildDice($playerInstance, 'p');
  const enemyDie = buildDice($enemyInstance, 'e');

  let activePlayerDie: Array<DieType> = $state.raw([]);
  let dieInBoard: Array<DieType> = $state.raw([]);
  let availableDie: Array<DieType> = [];
  let dieElements: DieElement[] = [];
  let diceContainer: HTMLDivElement;

  /*
    A store rather than $state. Dice are dropped into `technique.selected` in
    place, so a technique is the same object before and after, and an {#each}
    in runes mode would not redraw it. An {#each} over a store keeps the legacy
    rule -- same object, still checked -- and `$availableTechniques =
    $availableTechniques` is how a change in place is announced.
  */
  const availableTechniques = writable<Array<Technique>>([]);

  let rerollMax: number = $state(0);
  let rerollRemaining: number = $state(0);

  techniqueHistory.length = 0;

  function updateDice() {
    lockedDieUI = lockedDie.filter((die) => die);
    dieInBoard = dieInBoard.filter((die) => die);
  }
  window.addEventListener('updateDice', updateDice);

  function onUpdatePlayers() {
    updatePlayers();
    checkDied();
  }

  function updatePlayers() {
    $enemyInstance = $enemyInstance;
    $playerInstance = $playerInstance;
  }
  window.addEventListener('updatePlayers', onUpdatePlayers);

  function lockDie(die: DieType) {
    // die.locked = true;
    if (!lockedDie.includes(die)) {
      lockedDie.push(die);
      dieInBoard.splice(dieInBoard.indexOf(die), 1);
      updateDice();
    }
  }

  function dropDieOnTray(dropData: DropEvent) {
    switch (gameState) {
      case BattleState.Rolling:
        lockDie(dropData.item);
        break;

      case BattleState.ActionPhase:
        // remove die from all techniques
        $availableTechniques.forEach((technique) => {
          if (technique.selected) {
            technique.selected.forEach((dieArray, index) => {
              if (technique.selected && dieArray && dieArray[0]?.index === dropData.item.index) {
                dieArray.pop();
              }
            });
          }
        });

        lockedDie.push(dropData.item);
        updateDice();
        updateValidTechniques();
        break;
    }
    return true;
  }

  function unlockDie(die: DieType) {
    // prevent unlocking broken die
    if (die.value()[0] === DieFace.Broken) {
      return;
    }
    if (dieInBoard.includes(die)) {
      return;
    }
    // die.locked = false;
    dieInBoard.push(die);
    lockedDie.splice(lockedDie.indexOf(die), 1);
    updateDice();
  }

  function dropDieOnBoard(dropData: DropEvent) {
    unlockDie(dropData.item);

    let posX = dropData.position.x;
    let posY = dropData.position.y;

    if (diceContainer) {
      posX -= diceContainer.getBoundingClientRect().left;
      posY -= diceContainer.getBoundingClientRect().top;
    }

    positions[dropData.item.index][2] = posX;
    positions[dropData.item.index][3] = posY;

    return true;
  }

  function dropDieOnTechnique(dropData: DropEvent, technique: Technique) {
    if (technique.possible === false) {
      return false;
    }

    // If the die is in a technique already, remove it first
    $availableTechniques.forEach((t) => {
      if (t.selected) {
        t.selected.forEach((dieArray, index) => {
          if (t.selected && dieArray && dieArray[0]?.index === dropData.item.index) {
            dieArray.pop();
          }
        });
      }
    });

    // check if the die can be used in the technique
    let positions = fitDieInTechnique(dropData.item, technique);

    if (!positions) {
      console.warn('Die cannot be used in technique', dropData.item, technique);
      return false;
    }

    positions.forEach((pos, index) => {
      if (!technique.selected) {
        technique.selected = [];
      }
      if (!technique.selected[pos]) {
        technique.selected[pos] = [];
      }
      if (technique.selected[pos].length == 0) {
        if (index == 0) {
          technique.selected[pos].push(dropData.item);
        } else {
          let dieClone: DieType = dropData.item.clone();
          // dieClone.tags.push('ghost');
          dieClone.id = 'c' + randomInt(100, 10000);
          dieClone.material = DieMaterial.Cloned;
          technique.selected[pos].push(dieClone);
        }
      }
    });

    // updateValidTechniques();
    $availableTechniques = $availableTechniques;

    // remove die from lockedDie
    if (lockedDie.includes(dropData.item)) {
      lockedDie.splice(lockedDie.indexOf(dropData.item), 1);
    }
    updateDice();
    return true;
  }

  function clickDie(die: DieType) {
    switch (gameState) {
      case BattleState.Rolling:
        unlockDie(die);
        break;

      case BattleState.ActionPhase:
        /* if(!die.used){
          die.selected = !die.selected;
          updateValidTechniques();
          updateDice();
        } */
        break;
    }
  }

  async function roll() {
    let proms: Array<Promise<any>> = [];
    dieElements.forEach((die: DieElement, index: number) => {
      if (die && die.roll && dieInBoard.includes(die.getDie())) {
        proms.push(die.roll());
      }
    });
    moveDie(dieElements.length);

    if (proms.length <= 2) {
      playAudio(SoundType.DiceSingle);
    } else if (proms.length > 2) {
      playAudio(SoundType.DiceMultiple);
    }

    getModifiers($activePlayer, 'rollStart').forEach((mod) => {
      if (mod.rollStart) {
        mod.rollStart(dieElements, dieInBoard, rerollMax - rerollRemaining);
      }
    });

    return Promise.all(proms)
      .then((res) => {
        res.forEach((result, index) => {
          if (result === false) {
            lockDie(dieInBoard[index]);
          }
        });

        let proms2: Array<Promise<any>> = [];

        getModifiers($activePlayer, 'rollMid').forEach((mod) => {
          if (mod.rollMid) {
            mod.rollMid(dieElements, dieInBoard, proms2);
          }
        });

        return Promise.all(proms2);
      })
      .then(() => {
        handleInstant(dieInBoard);

        getModifiers($activePlayer, 'rollEnd').forEach((mod) => {
          if (mod.rollEnd) {
            mod.rollEnd(dieElements, dieInBoard);
          }
        });

        updatePlayers();
        updateDice();
        checkDied();

        if (rerollRemaining <= 0) {
          lockin();
        }
      });
  }

  function reroll() {
    if (rerollRemaining <= 0) {
      return;
    }

    let rolling = false;
    dieElements.forEach((dieElement) => {
      if (dieElement && dieElement.isRolling()) {
        rolling = true;
      }
    });
    if (rolling) {
      return;
    }

    rerollRemaining--;

    return roll();
  }

  function lockin() {
    for (var i = dieInBoard.length - 1; i >= 0; i--) {
      lockDie(dieInBoard[i]);
    }
    setState(BattleState.ActionPhase);
    updateValidTechniques();
    updateDice();

    $dieHistory = lockedDie.map((die) => die.clone());
    dieHistory.clear();
  }

  function handleInstant(dice: Array<DieType>) {
    // filter to unlocked die
    dice
      .filter((die) => die)
      .forEach(async (die) => {
        var techniques = filterTechniques([die]);
        getModifiers($activePlayer, 'getTechniques').forEach((mod) => {
          if (mod.getTechniques) {
            mod.getTechniques(techniques).map((technique) => {
              techniques.push(technique);
            });
          }
        });

        var technique = techniques
          .filter((technique) => {
            return technique.tags.includes('instant');
          })
          .find((technique) => {
            var ingredients = die.value();
            return technique.ingredients.toString() == ingredients.toString();
          });

        if (!technique) {
          return;
        }

        var able = true;
        getModifiers($activePlayer, 'canUseTechnique').forEach((mod) => {
          if (mod.canUseTechnique && technique) {
            var result = mod.canUseTechnique(technique, $activePlayer, $inactivePlayer);
            if (result && result.able === false) {
              able = false;
            }
          }
        });
        getModifiers($inactivePlayer, 'canInterrupt').forEach((mod) => {
          if (mod.canInterrupt && technique) {
            var result = mod.canInterrupt(technique, $activePlayer, $inactivePlayer);
            if (result && result.able === false) {
              able = false;
            }
          }
        });

        if (able) {
          // Awaiting on the array
          const uses: Array<Modifier> = getModifiers($activePlayer, 'useTechnique');
          for (let use of uses) {
            if (use.useTechnique) {
              /* await  */ use.useTechnique(technique, $inactivePlayer, $activePlayer, [die]);
            }
          }

          await technique.activate($inactivePlayer, $activePlayer, [die]);
        }

        addHistory(technique);

        updateDice();
        updatePlayers();
        checkDied();
      });
  }

  function selectIngredients(technique: Technique) {
    if (technique.possible === false) {
      return;
    }

    var dice = isTechniquePossible([], lockedDie, technique);

    if (dice && dice.length) {
      dice.forEach((die) => {
        setTimeout(() => {
          dropDieOnTechnique({ item: die, position: { x: 0, y: 0 } } as DropEvent, technique);
        }, 0);
      });
    }
  }

  async function execute(technique: Technique) {
    enemyInstance.put(clonePlayer($enemyInstance));
    playerInstance.put(clonePlayer($playerInstance));

    // Only allow recipes that are valid
    if (!technique.possible) {
      return;
    }

    // Select all required die
    var dice = technique.selected?.flat();

    updateDice();

    var able = true;
    getModifiers($activePlayer, 'canUseTechnique').forEach((mod) => {
      if (mod.canUseTechnique) {
        var result = mod.canUseTechnique(technique, $activePlayer, $inactivePlayer);
        if (result && result.able === false) {
          able = false;
        }
      }
    });
    getModifiers($inactivePlayer, 'canInterrupt').forEach((mod) => {
      if (mod.canInterrupt) {
        const result = mod.canInterrupt(technique, $activePlayer, $inactivePlayer);
        if (result && result.able === false) {
          able = false;
        }
      }
    });

    if (able) {
      // Awaiting on the array
      const uses = getModifiers($activePlayer, 'useTechnique');
      for (let use of uses) {
        if (use.useTechnique) {
          use.useTechnique(technique, $inactivePlayer, $activePlayer, dice || []);
        }
      }

      await technique.activate($inactivePlayer, $activePlayer, technique.selected);
    }
    technique.selected?.forEach((dieArray) => {
      dieArray.length = 0;
    });

    dieHistory.put(lockedDie.map((die) => die.clone()));
    removeDieFromTechiques();
    updateDice();

    addHistory(technique);

    updatePlayers();
    checkDied();
    availableDie = [...lockedDie];
    updateValidTechniques();

    continueTutorial({ type: TutorialEvent.TechniqueUsed, value: true });
  }

  function checkDied() {
    getModifiers($enemyInstance, 'checkDied').forEach((mod) => {
      if (mod.checkDied) {
        mod.checkDied();
      }
    });

    getModifiers($playerInstance, 'checkDied').forEach((mod) => {
      if (mod.checkDied) {
        mod.checkDied();
      }
    });

    // Check health
    if ($playerInstance.healthCurrent <= 0 && !$options.invincible) {
      endGame(BattleState.Lose);
    }
    if ($enemyInstance.healthCurrent <= 0) {
      endGame(BattleState.Win);
    }
  }

  function undo() {
    if (playerInstance.count() <= 0) {
      setState(BattleState.Rolling);
      // Back on the board with every die, not just the ones the clash locked:
      // the dice locked by hand during the roll were stranded in the tray.
      // unlockDie() still keeps a broken die where it is.
      [...lockedDie].forEach(unlockDie);

      return;
    }
    //reset player states
    playerInstance.undo();
    enemyInstance.undo();

    // reset die
    dieHistory.undo();
    lockedDie.length = 0;
    $dieHistory.forEach((die: DieType, index: number) => (lockedDie[index] = die.clone()));
    techniqueHistory.shift();

    techniqueHistoryUI = [...techniqueHistory];

    // reset recipes
    removeDieFromTechiques();
    updateDice();
    availableDie = [...lockedDie];
    updateValidTechniques();
  }

  function setState(newState: BattleState) {
    gameState = newState;
    continueTutorial({ type: TutorialEvent.BattleStateChange, value: newState });

    switch (gameState) {
      case BattleState.ActionPhase:
        availableDie = [...lockedDie];
        getModifiers($activePlayer, 'actionPhaseStart').forEach((mod) => {
          if (mod.actionPhaseStart) {
            mod.actionPhaseStart();
          }
        });
        break;
    }
  }

  function endTurn() {
    getModifiers($activePlayer, 'endTurn').forEach((mod) => {
      if (mod.endTurn) {
        mod.endTurn();
      }
    });
    checkDied();

    // swap player
    isPlayerActive = !isPlayerActive;
    syncActivePlayers();

    lockedDie.length = 0;
    updatePlayers();
    startTurn();
  }

  function startTurn() {
    setState(BattleState.Rolling);
    playerInstance.clear();
    enemyInstance.clear();
    dieHistory.clear();

    // reset dice
    /* dieInBoard = $activePlayer.dice.map((die: string, index: number) => {
      return createDie(die, index, 'p'+index);
    }); */

    let dieCreateList: Array<DieCreation> = clone($activePlayer.dice);

    getModifiers($activePlayer, 'getDice').forEach((mod) => {
      if (mod.getDice) {
        mod.getDice(dieCreateList).forEach((die) => {
          dieCreateList = dieCreateList.concat(die);
        });
      }
    });

    dieInBoard = dieCreateList.map((dieCreate: DieCreation, index: number) => {
      return createDie(dieCreate, index, 'p' + index);
    });
    activePlayerDie = dieCreateList.map((dieCreate: DieCreation, index: number) => {
      return createDie(dieCreate, index, 'p' + index);
    });

    /* getModifiers($activePlayer, 'updateDie').forEach(mod => {
      mod.updateDie(dieInBoard);
    }); */

    moveDie(dieInBoard.length, true);

    lockedDie.length = 0;

    // reset rerolls
    rerollMax = $activePlayer.rerollMax;

    getModifiers($activePlayer, 'beforeStartTurn').forEach((mod) => {
      if (mod.beforeStartTurn) {
        mod.beforeStartTurn();
      }
    });
    getModifiers($activePlayer, 'startTurn').forEach((mod) => {
      if (mod.startTurn) {
        let res = mod.startTurn({
          dice: dieInBoard,
          rerollMax,
          activePlayer: $activePlayer,
          inactivePlayer: $inactivePlayer,
        });

        if (res) {
          if (res.dice) {
            dieInBoard = res.dice;
          }
          if (res.rerollMax) {
            rerollMax = res.rerollMax;
          }
        }
      }
    });

    rerollRemaining = rerollMax;

    addHistory({
      player: undefined,
      title: 'Change Turns',
      text: '',
      ingredients: ['endturn'],
    });

    setTimeout(() => {
      roll();

      if (!isPlayerActive || $options.autoPlay) {
        setTimeout(AI, 1000);
      }
    }, 1000);

    /* addModifier($activePlayer, StatusEffect.Stance, 2, {
      stance: Stances.Interrupt,
    }); */
  }

  function endGame(gameState: BattleState.Win | BattleState.Lose) {
    if (gameState == BattleState.Win) {
      data.battle.outcome = 'win';
      setState(BattleState.Win);
      alert({
        title: 'Victory',
        text: 'You have defeated the enemy',
        buttonText: 'Continue',
      }).then(() => {
        advance();
      });
    } else {
      data.battle.outcome = 'lose';
      setState(BattleState.Lose);
      alert({
        title: 'Defeat',
        text: 'You have been defeated by the enemy',
        buttonText: 'Continue',
      }).then(() => {
        advance();
      });
    }

    if (actionHistory.length) {
      addHistory({
        title: 'End Game',
        text: '',
        ingredients: ['grave'],
      });
    }
  }

  function addHistory(data: HistoryItem) {
    techniqueHistory.unshift({
      player: $activePlayer,
      title: data.title,
      text: data.text,
      ingredients: data.ingredients,
      tags: data.tags,
      actions: [...actionHistory],
    });

    actionHistory.length = 0;

    techniqueHistoryUI = [...techniqueHistory];
  }

  function advance() {
    screens.set(Screen.BattleOver, data);
  }

  let lastWidth = 0;
  let lastHeight = 0;
  function moveDie(count: number, reset: boolean = false) {
    if (!diceContainer || !(diceContainer.clientWidth || lastWidth)) {
      setTimeout(() => {
        moveDie(count, reset);
      }, 100);

      for (let i: number = 0; i < count; i++) {
        positions[i] = new Array(4).fill(randomFloat(10, 80));
      }
      return;
    }

    for (let i: number = 0; i < count; i++) {
      if (reset) {
        positions[i] = [randomFloat(10, 80), randomFloat(10, 80)];
      } else {
        positions[i][0] = positions[i][0] + randomFloat(-15, 15);
        positions[i][1] = positions[i][1] + randomFloat(-15, 15);

        // Bind to screen
        positions[i][0] = clamp(positions[i][0], 10, 80);
        positions[i][1] = clamp(positions[i][1], 10, 80);
      }

      positions[i][2] = positions[i][0];
      positions[i][3] = positions[i][1];

      if (lastWidth && lastHeight) {
        positions[i][2] = (positions[i][0] * lastWidth) / 100;
        positions[i][3] = (positions[i][1] * lastHeight) / 100;
      } else if (diceContainer && diceContainer.clientWidth && diceContainer.clientHeight) {
        lastWidth = diceContainer.clientWidth;
        lastHeight = diceContainer.clientHeight;
        positions[i][2] = (positions[i][0] * lastWidth) / 100;
        positions[i][3] = (positions[i][1] * lastHeight) / 100;
      }

      // test position against previous positions for distance
      for (let j = 0; j < i; j++) {
        let dist = distance(positions[i][0], positions[i][1], positions[j][0], positions[j][1]);
        // let dist = Math.sqrt(Math.pow(positions[i][0]-positions[j][0], 2) + Math.pow(positions[i][1]-positions[j][1], 2));
        if (dist < 20) {
          i--;
          continue;
        }
      }
    }
    updateDice();
  }

  function resize() {
    lastWidth = diceContainer.clientWidth;
    lastHeight = diceContainer.clientHeight;
    moveDie(dieInBoard.length);
  }
  onMount(() => {
    window.addEventListener('resize', resize);
    startTurn();
    updatePlayers();
    updateDice();
    checkDied();

    return () => {
      window.removeEventListener('resize', resize);

      // None of these were released. A board is built afresh for every battle,
      // so each one left two window listeners and two store subscriptions
      // behind, all holding the finished board in memory.
      window.removeEventListener('updateDice', updateDice);
      window.removeEventListener('updatePlayers', onUpdatePlayers);
      unsubscribePlayer();
      unsubscribeEnemy();
    };
  });

  function updateValidTechniques() {
    let techniques = getAvailableTechniques(availableDie).map((technique) => {
      // technique.selected = new Array(technique.ingredients.length);
      validateTechnique(technique, availableDie);
      return technique;
    });

    getModifiers($activePlayer, 'getTechniques').forEach((mod) => {
      if (mod.getTechniques) {
        mod.getTechniques(techniques).map((technique) => {
          // Granted techniques miss the validation above. Unvalidated, they are
          // never `possible`, and execute() and the AI both pass them over.
          validateTechnique(technique, availableDie);
          techniques.push(technique);
        });
      }
    });

    // Sorts techniques by possibility
    techniques = sortTechniques(techniques);
    $availableTechniques = techniques;

    return techniques;
  }

  /**
   * Whether a die is actually sitting in this slot.
   *
   * `technique.selected[index]` is an array, so it is truthy the moment the
   * slot has been touched -- and execute() empties those arrays in place rather
   * than dropping them, so after one use every slot read as filled forever.
   */
  function slotFilled(technique: Technique, index: number) {
    return !!technique.selected?.[index]?.length;
  }

  function techniqueActive(technique: Technique) {
    //check that the length of the selected array matches the ingredients array
    if (!technique.selected || technique.selected.flat().length != technique.ingredients.length) {
      return false;
    }
    return true;
  }

  function clickTechnique(technique: Technique) {
    if (techniqueActive(technique)) {
      execute(technique);
      technique.selected?.forEach((item) => {
        item.length = 0;
      });
    }
  }

  function removeDieFromTechiques() {
    $availableTechniques.forEach((technique) => {
      if (technique.selected) {
        technique.selected.forEach((dieArray, index) => {
          if (technique.selected && dieArray && dieArray[0] && !lockedDie.includes(dieArray[0])) {
            let die = technique.selected[index].pop();
            if (die && lockedDie.indexOf(die) == -1) {
              lockedDie.push(die);
            }
          }
        });
      }
    });
  }

  let AITimeout: any;
  let waitAI = false;
  function AI() {
    let currentTime = performance.now();

    switch (gameState) {
      case BattleState.Rolling:
        var locked = false;
        for (var i = dieInBoard.length - 1; i >= 0; i--) {
          var target = 4;
          if (rerollRemaining <= 1) {
            target = 2;
          }
          if (
            dieInBoard[i].value()[0] !== DieFace.Blank &&
            dieInBoard[i].value()[0] !== DieFace.Broken
          ) {
            if (dieInBoard[i].activeFace >= target) {
              lockDie(dieInBoard[i]);
              locked = true;
            }
          }
        }
        if (!locked) {
          reroll();
        }
        waitAI = true;
        break;

      case BattleState.ActionPhase:
        if (waitAI) {
          waitAI = false;
          break;
        }
        let techniques = updateValidTechniques().filter((technique) => technique.possible);

        if (techniques.length) {
          const mazlovs = calculateMazlovs($activePlayer, $inactivePlayer);
          const technique = chooseTechnique(techniques, mazlovs);

          // if the technique has no die, call selectIngredients
          if (technique && (!technique.selected || technique.selected.flat().length == 0)) {
            selectIngredients(technique);
            AITimeout = setTimeout(() => {
              clickTechnique(technique);
              AITimeout = setTimeout(AI, 1000);
            }, 1000);
            return;
          }

          if (!technique) {
            endTurn();
            return;
          }
          execute(technique);
        } else {
          endTurn();
          return;
        }
        break;
    }

    if (AITimeout) {
      clearTimeout(AITimeout);
    }
    AITimeout = setTimeout(AI, 1000);
  }
</script>

<div class="layout">
  <div class="column player">
    <Droppable onDrop={dropDieOnTray} itemArray={lockedDie}>
      <div class="column player">
        <div class="tray wood">
          {#if playerDie.length}
            <DiceTray
              dice={isPlayerActive ? lockedDieUI : playerDie}
              clickEvent={isPlayerActive ? clickDie : () => {}}
            />
          {/if}

          <HealthBar data={$playerInstance} />

          <!-- <button class='btn' on:click={()=>{endGame(BattleState.Win);}}>
            Autowin
            </button>
            <button class='btn' on:click={()=>{endGame(BattleState.Lose);}}>
              Autolose
            </button> -->
        </div>

        <Portrait data={$playerInstance} />
      </div>
    </Droppable>
  </div>

  <div class="column board background {data.battle.environment}">
    <HistoryBar history={techniqueHistoryUI} />

    <div
      class="dice-container {gameState == BattleState.Rolling ? '' : 'hidden'}"
      bind:this={diceContainer}
    >
      <Droppable
        onDrop={(item: DropEvent) => {
          return dropDieOnBoard(item);
        }}
        itemArray={dieInBoard}
      >
        {#each dieInBoard as die, index (die.id)}
          <Draggable
            left={positions[die.index][2]}
            top={positions[die.index][3]}
            onClick={isPlayerActive
              ? () => {
                  lockDie(die);
                }
              : () => {}}
            onHold={() => {
              peekedDie.set(die);
            }}
            onHoldEnd={() => {
              peekedDie.set(null);
            }}
            data={die}
          >
            <div
              class="slot slot-{die.index} board-die"
              in:receive={{ key: die.id }}
              out:send={{ key: die.id }}
            >
              <DieElement
                {die}
                bind:this={dieElements[die.index]}
                hover={true}
                showInfo={true}
                allDice={activePlayerDie}
              />
            </div>
          </Draggable>
        {/each}
      </Droppable>
    </div>

    {#if gameState == BattleState.ActionPhase}
      <div class="skills">
        {#each $availableTechniques as technique, index (technique.title)}
          <div animate:flip={{ duration: 400 }} role="button" tabindex="0">
            <Droppable
              onDrop={(item: DropEvent) => {
                return dropDieOnTechnique(item, technique);
              }}
              fillHeight={false}
              itemArray={technique.selected ? technique.selected.flat() : []}
            >
              <div
                class="technique wood
            {technique.possible ? 'valid' : 'invalid'}
            {techniqueActive(technique) ? 'active' : ''}
          "
              >
                <div class="ingredients">
                  {#each technique.ingredients as text, index}
                    <div
                      class="ingredient
                        {slotFilled(technique, index) ? 'filled' : 'empty'}"
                      role="button"
                      tabindex="0"
                      title="Fill this recipe from your locked dice"
                      onmousedown={() => {
                        selectIngredients(technique);
                      }}
                      onkeydown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          selectIngredients(technique);
                        }
                      }}
                    >
                      <!-- The face that belongs in this slot, showing through
                           until a die covers it. -->
                      <Icon name={text} class="ghost" />

                      {#if technique.selected}
                        {#each technique.selected[index] as die, index2}
                          <Draggable
                            left={0}
                            top={0}
                            data={die}
                            freeMovement={false}
                            locked={die.material == DieMaterial.Cloned}
                          >
                            <div in:receive={{ key: die?.id }} out:send={{ key: die?.id }}>
                              {#if die}
                                <DieElement {die} die3D={false} hover={false} />
                              {/if}
                            </div>
                          </Draggable>
                        {/each}
                      {/if}
                    </div>
                  {/each}

                  {#if technique.requirements}
                    {#each technique.requirements as requirement, index}
                      {#if requirement.icon}
                        <div
                          class="requirement {requirement.test($activePlayer, $inactivePlayer)
                            ? 'passed'
                            : ''}"
                        >
                          <Icon name={requirement.icon} width="40px" height="40px" />
                          {#if requirement.iconBonus}
                            <Icon name={requirement.iconBonus} width="60px" height="60px" />
                          {/if}

                          <Hover>
                            <div class="hover-icon">
                              <Icon name={requirement.icon} width="40px" height="40px" />
                              {#if requirement.iconBonus}
                                <Icon name={requirement.iconBonus} width="60px" height="60px" />
                              {/if}
                            </div>
                            <br />

                            <p>{requirement.text}</p>
                          </Hover>
                        </div>
                      {/if}
                    {/each}
                  {/if}
                </div>
                <div class="description">
                  <div class="text">
                    <h2>
                      {technique.title}
                    </h2>
                    <Text data={technique.text} />
                  </div>

                  <!-- Swaps in over the description once the recipe is full,
                       which is what keeps the row this short. -->
                  <div class="activate">
                    <Chrome
                      click={() => {
                        clickTechnique(technique);
                      }}
                      chroming="gold"
                      edge="round"
                      thickness="thick"
                      gloss={true}
                    >
                      <div class="action">Activate</div>
                    </Chrome>
                  </div>
                </div>
              </div>
            </Droppable>
          </div>
        {/each}
      </div>
    {/if}

    <!--
      One bar for both phases, so they cannot drift apart again: the two ends
      change with the phase, and options and the glossary are always between.
    -->
    {#if gameState == BattleState.Rolling || gameState == BattleState.ActionPhase}
      <div class="actions actions-3">
        {#if gameState == BattleState.Rolling}
          <Chrome click={reroll} chroming="silver" edge="round" thickness="thick" gloss={true}>
            <div class="action reroll"><Icon name="reroll" /></div>
            <p class="count">{rerollRemaining}</p>
          </Chrome>
        {:else}
          <Chrome click={undo} chroming="silver" edge="round" thickness="thick" gloss={true}>
            <div class="action undo"><Icon name="undo" /></div>
          </Chrome>
        {/if}

        <div class="menu">
          <Chrome
            click={() => {
              openOptions({});
            }}
            chroming="silver"
            edge="round"
            thickness="thin"
            gloss={true}
          >
            <div class="action small"><Icon name="options" /></div>
          </Chrome>
          <Chrome click={openGlossary} chroming="silver" edge="round" thickness="thin" gloss={true}>
            <div class="action small"><Icon name="glossary" /></div>
          </Chrome>
        </div>

        <!-- Gold, like Activate: the button that moves the game on. -->
        {#if gameState == BattleState.Rolling}
          <Chrome click={lockin} chroming="gold" edge="round" thickness="thick" gloss={true}>
            <div class="action lockin"><Icon name="clash" /></div>
          </Chrome>
        {:else}
          <Chrome
            click={isPlayerActive ? endTurn : () => {}}
            chroming="gold"
            edge="round"
            thickness="thick"
            gloss={true}
          >
            <div class="action endturn"><Icon name="endturn" /></div>
          </Chrome>
        {/if}
      </div>
    {/if}
  </div>

  <div class="column enemy">
    <Portrait data={$enemyInstance} />

    <div class="tray wood">
      <HealthBar data={$enemyInstance} reverse={true} />

      {#if enemyDie.length}
        <DiceTray clickEvent={() => {}} dice={!isPlayerActive ? lockedDieUI : enemyDie} />
      {/if}
    </div>
  </div>
</div>

<Particles />
